import { botPath, isBotSystem, parseBotPath, type BotRef, type BotSection } from './botNav.ts';

/**
 * A1 (chatbot_v2_mono docs/superpowers/specs/2026-10-06-ai-console-v2-first-design.md): every bot-scoped hub call
 * carries the bot's system, and no page acts on a bot nobody chose (the default used to be chatbot/7598).
 */
const q = (system: string) => `?system=${encodeURIComponent(system)}`;

/** Per-bot generic-intent overrides — keyed by (system, botId, code) since A1. */
export const botConfigListPath = (bot: BotRef): string =>
  `/api/generic-intents/bot-config/${encodeURIComponent(bot.botId)}${q(bot.system)}`;

export const botConfigDeletePath = (bot: BotRef, code: string): string =>
  `/api/generic-intents/bot-config/${encodeURIComponent(bot.botId)}/${encodeURIComponent(code)}${q(bot.system)}`;

/** PATCH /api/bots/:botId — the access check authorizes the QUERY system, so it goes there. */
export const botPatchPath = (bot: BotRef): string => `/api/bots/${encodeURIComponent(bot.botId)}${q(bot.system)}`;

/** The chosen bot, or null until a known system and a bot id are both selected. */
export function selectedBot(scope: { system: string; botId: string }): BotRef | null {
  return isBotSystem(scope.system) && scope.botId ? { system: scope.system, botId: scope.botId } : null;
}

/** Picker / list tag; null while no known system is chosen (an empty system is not "v1"). */
export function systemTag(system: string): 'v1' | 'v2' | null {
  if (system === 'chatbot_v2') return 'v2';
  if (system === 'chatbot') return 'v1';
  return null;
}

const BOT_NAV: { section: BotSection; label: string }[] = [
  { section: 'intents', label: 'Intents' },
  { section: 'review', label: 'Review' },
  { section: 'moderation', label: 'Moderation' },
  { section: 'config', label: 'Settings' },
  { section: 'knowledge', label: 'Knowledge' },
  { section: 'quality', label: 'Quality' },
  { section: 'tools', label: 'Tools' },
  { section: 'cache', label: 'Cache' },
  { section: 'usage', label: 'Usage' },
  { section: 'forms', label: 'Forms' },
  { section: 'memory', label: 'Memory' },
];

/** Sidebar bot-workspace links; `to` is null until a bot is chosen (no `/bots/chatbot//…` links). */
export function botNavItems(scope: { system: string; botId: string }): { section: BotSection; label: string; to: string | null }[] {
  const bot = selectedBot(scope);
  return BOT_NAV.map((i) => ({ ...i, to: bot ? botPath(bot, i.section) : null }));
}

/** Flat pages that land on a bot section (`/` → Settings); with no bot chosen they show the choose-a-bot landing. */
const LANDING_SECTIONS: Record<string, BotSection> = { '/': 'config', '/forms': 'forms', '/memory': 'memory' };

/** The section a newly chosen bot opens on: the current workspace section, or the flat page's section. */
export function pickerSection(pathname: string): BotSection {
  return parseBotPath(pathname)?.section ?? (Object.hasOwn(LANDING_SECTIONS, pathname) ? LANDING_SECTIONS[pathname] : 'config');
}

/** The picker opens by itself on a choose-a-bot landing (fresh browser, no bot chosen). */
export function pickerAutoOpen(scope: { system: string; botId: string }, pathname: string): boolean {
  return !selectedBot(scope) && Object.hasOwn(LANDING_SECTIONS, pathname);
}
